/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { Fragment, useCallback, useContext, useMemo, useState } from 'react';
import { Header } from 'views/savedViews/header';
import { Divider, NonIdealState, Classes, Icon, Button } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Dashboard } from '/views/dashboard';
import { LAYOUT_OPTIONS } from '/views/explorer/layoutOptions';
import { SavedStatesActionCreator } from '/hooks/savedViews/actions';
import { useSavedStateSelectors } from '/hooks/savedViews/hooks';
import { WorkspaceContext } from '/views/workspace/workspaceContext';
import { Dialog } from 'components/dialogs';
import { EditDashboard } from '/views/savedViews/editDashboard';
import { ManageViewsActionCreator, ManageViewsStateContext } from './useManageViewsState';
import { v4 as uuid } from "uuid";

function cardCountToLayout(layoutKey, count) {
	const targetCardCount = count === 0 ? 1 : count;
	if (layoutKey !== null) {
		return LAYOUT_OPTIONS[layoutKey];
	}
	return Object.values(LAYOUT_OPTIONS).find(
		({ cardCount }) => cardCount === targetCardCount
	);
}

export function RenderDashboard({ toggleFavorite }) {
	const { dispatch: savedStatesDispatch } = useSavedStateSelectors();
	const { 
		state: {
			isCreating,
			isEditing,
			selectedView: dashboard,
		},
		dispatch: manageViewsDispatch
	} = useContext(ManageViewsStateContext);

	
	const [isDeleteDashboardModalOpen, setDeleteDashboardModalOpen] = useState(false);

	const openDeleteModal = () => { 
		setDeleteDashboardModalOpen(true);
	}

	const closeDeleteModal = () => { 
		setDeleteDashboardModalOpen(false); 
	}

	// Delete the view
	const handleDelete = useCallback(() => {
		if(!dashboard?.metaData?.id) return;
		manageViewsDispatch(ManageViewsActionCreator.setSelectedView(null));
		savedStatesDispatch(
			SavedStatesActionCreator.deleteView('dashboards', dashboard.metaData.id)
		);
		closeDeleteModal(); 
	}, [dashboard, savedStatesDispatch, manageViewsDispatch]);

	const getNewDashboard = () => ({
		cards: [], 
		layout: {
			layoutStyle: {
				key: LAYOUT_OPTIONS.ONE.key,
				cardCount: 1
			}
		},
		metaData: {
			id: uuid(),
			name: 'New Dashboard',
			type: 'dashboard'
		}
	});

	const handleEdit = () => {
		manageViewsDispatch(ManageViewsActionCreator.startEditing());
		const layoutKey = dashboard?.layout?.layoutStyle?.key || null;
		manageViewsDispatch(ManageViewsActionCreator.setLayout(cardCountToLayout(layoutKey, dashboard?.cards.length)));
	}

	const style = useMemo(() => dashboard?.layout?.layoutStyle ? LAYOUT_OPTIONS[dashboard?.layout.layoutStyle.key].styles : {}, [dashboard]);

	return (
		<WorkspaceContext.Provider value={null}>
			{isEditing ? (
				<EditDashboard
					dashboard={dashboard}
					onDashboardFinished={() => {
						manageViewsDispatch(ManageViewsActionCreator.stopEditing());
					}}
					isCreating={isCreating}
					toggleFavorite={toggleFavorite}
				/>
			) : isCreating ? (
				<EditDashboard
					dashboard={getNewDashboard()}
					onDashboardFinished={() => {
						manageViewsDispatch(ManageViewsActionCreator.stopCreating());
					}}
					isCreating={isCreating}
				/>
			) : (
				<Fragment>
					<Header
						viewName={dashboard.metaData.name}
						isFavorite={dashboard.metaData?.isFavorite}
						onDelete={openDeleteModal}
						onEdit={handleEdit}
						toggleFavorite={toggleFavorite}
					/>
					{isDeleteDashboardModalOpen &&
						<Dialog style={styles.Dialog} 
							title={"Delete Dashboard"} 
							body = {
								<div className={Classes.DIALOG_BODY}>
									<p>Are you sure you want to delete this dashboard? It cannot be undone.</p> 
								</div> 
							}
							icon={<Icon icon={IconNames.TRASH} intent={"danger"}/>}
							onClose={closeDeleteModal} 
							footer = {
								<div className={Classes.DIALOG_FOOTER} >
									<Button minimal={true} css={css `.bp3-button-text {color: #5c7080;}`} onClick={closeDeleteModal}>Cancel</Button> 
									<Button outlined={true} onClick={handleDelete} intent="primary">Delete</Button>
								</div>
							}
						/>
					}
					<Divider css={css`margin-left: -20px; margin-right: -20px; padding-top: 10px;`}/>
					{dashboard.cards && dashboard.cards.length === 0 ? (
						<NonIdealState
							icon="clean"
							title={'Empty Dashboard'}
							description={'Edit to add widgets'}
						/>
					) : (
						<Dashboard configuration={dashboard?.cards} cssStyles={style} />
					)}
				</Fragment>
			)}
		</WorkspaceContext.Provider>
	);
}

const styles = { 
	Dialog: css`
	.bp3-button{ 
		margin-left: 5px; 
	}

	.bp3-dialog-footer{ 
		text-align: right; 
	}

	background: white; 
`,	
}

